import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Mail, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import Footer from "../components/Footer";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1000));

    toast.success("Message sent!", {
      description: "We'll get back to you as soon as possible.",
    });

    setFormData({ name: "", email: "", phone: "", message: "" });
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Header */}
      <section className="py-16 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-unbounded text-4xl md:text-5xl font-bold text-foreground mb-4">
              Contact Us
            </h1>
            <p className="font-outfit text-lg text-muted-foreground">
              Have questions? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="bg-card rounded-2xl border border-border p-6 md:p-8">
                <h2 className="font-unbounded text-xl font-semibold text-foreground mb-6">
                  Send us a message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="font-outfit">
                      Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      className="mt-2 bg-muted border-border focus:border-primary"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="font-outfit">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                      className="mt-2 bg-muted border-border focus:border-primary"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="font-outfit">
                      Phone (Optional)
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="contact-phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="mt-2 bg-muted border-border focus:border-primary"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-outfit">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      rows={5}
                      required
                      className="mt-2 bg-muted border-border focus:border-primary resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={loading}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-outfit font-semibold py-6 text-lg rounded-xl"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
                        Sending...
                      </div>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col justify-center"
            >
              <h2 className="font-unbounded text-2xl font-bold text-foreground mb-8">
                Get in Touch
              </h2>

              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-unbounded text-lg font-semibold text-foreground mb-1">
                      Visit Us
                    </h3>
                    <p className="font-outfit text-muted-foreground">
                      1823 E Santa Fe St
                      <br />
                      Gardner, KS 66030
                    </p>
                    <a
                      href="https://maps.app.goo.gl/7jYJikWFQLMz9qhK9"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 font-outfit text-sm text-primary hover:underline"
                    >
                      Get Directions →
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-7 h-7 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-unbounded text-lg font-semibold text-foreground mb-1">
                      Call Us
                    </h3>
                    <a
                      href="tel:+19136051001"
                      className="font-outfit text-muted-foreground hover:text-primary transition-colors"
                    >
                      (913) 605-1001
                    </a>
                    <p className="font-outfit text-sm text-muted-foreground mt-1">
                      We're happy to answer any questions
                    </p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-7 h-7 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-unbounded text-lg font-semibold text-foreground mb-1">
                      Store Hours
                    </h3>
                    <div className="font-outfit text-muted-foreground">
                      <p>Monday - Sunday</p>
                      <p>10:00 AM - 8:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Contact */}
              <div className="mt-12 p-6 rounded-xl bg-primary/5 border border-primary/20">
                <h3 className="font-unbounded text-lg font-semibold text-foreground mb-3">
                  Prefer to call?
                </h3>
                <p className="font-outfit text-sm text-muted-foreground mb-4">
                  Our team is available during store hours to help with any
                  questions about products, orders, or general inquiries.
                </p>
                <Button
                  data-testid="call-store-contact-btn"
                  asChild
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <a href="tel:+19136051001">
                    <Phone className="w-4 h-4 mr-2" />
                    (913) 605-1001
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
